/**
 * ConfirmSignOffModal.js
 *
 * FT-196: Standardised confirm-only sign-off modal (Pattern 1)
 * For: Voyage sign-off, Logbook day sign-off, Drills
 *
 * Simple confirmation - no drawn signature required.
 * Reads current user from auth context (NOT editable).
 *
 * FT-285 Item 2 / §13.1 Q1: extended with OPTIONAL `timeField` /
 * `vesselTimezone` / `timeFieldLabel` props so the All Fast confirm step can
 * host the inline event-time field. Legacy callers (drill sign-off, logbook
 * day sign-off, voyage sign-off) pass nothing → no behavioural change.
 *
 * §13.2 BLOCKER fix: when `timeField` is supplied we await `onConfirm` and
 * only call `onClose()` after a clean success. If the result carries a
 * `boundaryError`, we surface it inline and keep the modal open so the
 * Master can correct the time. Without this gate, a voyage-bounds rejection
 * would close the modal before the user could see the error.
 *
 * §13.4: the timeField reset effect uses `[isOpen]` as deps with an
 * eslint-disable line — the reset callback is stable per `useInlineEventTime`
 * contract.
 */

import React, { useEffect, useState } from 'react';
import { X, Check, User, AlertCircle, AlertTriangle } from 'lucide-react';
import { usePermissions } from '../../hooks/usePermissions';
import InlineTimeField from '../tailwind/modules/operations/ActiveVoyage/InlineTimeField';

const ROLE_LABELS = {
  skipper: 'Master',
  fleet: 'Fleet Manager',
  super_admin: 'Administrator',
  crew: 'Crew'
};

const ConfirmSignOffModal = ({
  isOpen,
  onClose,
  onConfirm,
  title = 'Sign Off',
  description = 'You are confirming this record as reviewed and approved.',
  // FT-285 Item 2: optional inline event-time field. When present, the modal
  // gates the Confirm button on timeField.isValid and attaches a fresh
  // snapshot to the onConfirm payload as `timeOptions`. Legacy callers omit
  // these props and get the original behaviour.
  timeField = null,
  vesselTimezone = 'UTC',
  timeFieldLabel = 'Time'
}) => {
  const { currentUser } = usePermissions();

  // FT-285 §13.2: inline boundary-error banner. Only used when timeField is
  // supplied; legacy callers never set this.
  const [boundaryError, setBoundaryError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // FT-285 Item 2: reset the inline time field whenever the modal opens so a
  // re-open after Cancel doesn't carry stale edits forward.
  useEffect(() => {
    if (isOpen) {
      setBoundaryError(null);
      setSubmitting(false);
      if (timeField) timeField.reset();
    }
    // timeField.reset is stable per useInlineEventTime contract.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const userName = currentUser?.name || 'Unknown User';
  const userRole = ROLE_LABELS[currentUser?.role] || currentUser?.role || 'Unknown Role';

  // FT-285 §13.2 BLOCKER fix: when timeField is supplied, await onConfirm and
  // only close on clean success. Boundary errors surface inline, keeping the
  // modal open. Legacy path (no timeField) preserves the original sync flow.
  const handleConfirm = async () => {
    if (timeField && !timeField.isValid) return;
    if (submitting) return;

    if (timeField) {
      setSubmitting(true);
      setBoundaryError(null);
      try {
        const timeOptions = timeField.freshSnapshot();
        const result = await onConfirm({
          confirmedBy: userName,
          role: userRole,
          confirmedAt: new Date().toISOString(),
          timeOptions
        });
        if (result?.boundaryError) {
          setBoundaryError(result.boundaryError);
          return;
        }
        onClose();
      } finally {
        setSubmitting(false);
      }
      return;
    }

    // Legacy path — preserved exactly. Drill sign-off / logbook day sign-off /
    // voyage sign-off callers do not pass timeField and expect synchronous
    // close after onConfirm.
    onConfirm({
      confirmedBy: userName,
      role: userRole,
      confirmedAt: new Date().toISOString()
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-25" onClick={onClose} />

      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">{title}</h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            <p className="text-sm text-gray-600 mb-6">{description}</p>

            {/* FT-285 Item 2: optional inline event-time field, above the
                current-user card. Only rendered when the parent supplied a
                hook instance. */}
            {timeField && (
              <div className="mb-6">
                <InlineTimeField
                  timeField={timeField}
                  vesselTimezone={vesselTimezone}
                  label={timeFieldLabel}
                />
              </div>
            )}

            {/* FT-285 §13.2: inline boundary-error banner. Renders only when
                the parent returns { boundaryError } from onConfirm. */}
            {boundaryError && (
              <div
                role="alert"
                className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg mb-6"
              >
                <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-red-800">{boundaryError}</p>
              </div>
            )}

            {/* Current user display (read-only) */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                Signed off by:
              </p>
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 h-10 w-10 bg-cyan-100 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5 text-cyan-600" />
                </div>
                <div>
                  <p className="text-base font-medium text-gray-900">{userName}</p>
                  <p className="text-sm text-gray-500">{userRole}</p>
                </div>
              </div>
            </div>

            {/* Warning notice */}
            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-6">
              <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-800">
                This action will be logged and cannot be undone.
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={submitting || (timeField && !timeField.isValid)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Check className="h-4 w-4 mr-2" />
                Confirm Sign-Off
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmSignOffModal;
